import type { SearchType } from 'lib/dot/enum/searchType.ts';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { PageType, type PrevPageType } from 'lib/dot/enum/pageType.ts';
import {
	type SearchFiltersAnalytics,
	localFiltersToAnalyticsFilters,
	localityToAnalyticsLocality,
} from 'lib/dot/utils.ts';
import type { BooleanAnalytics, LocalityAnalytics } from 'lib/dot/types.ts';
import type { URLFilterParams } from 'utils/url.ts';
import type { LocalityData } from 'service/locality/model.ts';
import { HomepageTab } from 'lib/dot/enum/homepageTab.ts';
import { getSearchPageUrlParams } from 'lib/dot/utils/routeParams.ts';

export type SearchAnalyticsStore = Pick<SearchAnalytics, 'myProfile' | 'usedGoods' | 'sellerFilter' | 'sellerId'>;

export type SearchAnalytics = {
	action: 'search';
	categoryCount: number;
	categoryId: number;
	filters: SearchFiltersAnalytics;
	itemCount: number;
	locality?: LocalityAnalytics;
	myProfile: BooleanAnalytics;
	prevPageType: PrevPageType;
	queryValue?: string;
	searchType: SearchType;
	sellerFilter: BooleanAnalytics;
	sellerId?: number | string;
	tab?: HomepageTab | '';
	usedGoods: BooleanAnalytics;
};

export type SearchMeta = { searchType: SearchType; tab?: HomepageTab; usedGoods?: boolean };
// #if [DEV]
const SEARCH_KEYS = ['myProfile', 'prevPageType', 'sellerFilter', 'usedGoods'];
// #endif

export type SearchStoreInitializerData = {
	categoryCount?: number;
	itemCount?: number;
	myProfile: boolean;
	searchType?: SearchType;
	sellerFilter: boolean;
	sellerId?: number | string;
	usedGoods: boolean;
};

export function initSearchStore({
	categoryCount,
	itemCount,
	myProfile,
	sellerFilter,
	sellerId,
	usedGoods,
	searchType,
}: SearchStoreInitializerData) {
	const data: SearchAnalyticsStore = {
		...(categoryCount && { categoryCount }),
		...(itemCount && { itemCount }),
		...(searchType && { searchType }),
		...(sellerId && { sellerId }),
		myProfile: DOTHelper.sanitizeBoolean(myProfile),
		sellerFilter: DOTHelper.sanitizeBoolean(sellerFilter),
		usedGoods: DOTHelper.sanitizeBoolean(usedGoods),
	};
	DOTHelper.setStore(data);
}

/**
 *
 * @param filters
 * @param resolvedLocality
 * @param searchType
 * @param categoryCount
 * @param itemCount
 * @param useCurrentPageType Set to true for SPA, where searches do not change the actual page(Type), but the prevPageType at that moment should be the current pageType.
 * @param tab
 */
export function sendSearchAnalytics({
	categoryCount,
	filters,
	itemCount,
	resolvedLocality,
	searchType,
	tab,
	useCurrentPageType,
}: {
	categoryCount: number | undefined;
	filters: URLFilterParams;
	itemCount: number;
	resolvedLocality?: LocalityData;
	searchType: SearchType;
	tab?: HomepageTab;
	useCurrentPageType?: boolean;
}) {
	// #if [DEV]
	DOTHelper.checkStore(SEARCH_KEYS);
	// #endif

	const store = DOTHelper.getStore();
	if (categoryCount === undefined) categoryCount = store.categoryCount;
	DOTHelper.addToStore({ searchType, categoryCount, itemCount }); // searchType will be used for saving offerDetailMeta when clicking on an offerCard

	// Handle edge case, where analytics want `filter: usedGoods=1` when coming from tab usedGoods
	const filtersToSend = localFiltersToAnalyticsFilters(filters);
	if (tab === HomepageTab.USED) {
		filtersToSend.usedGoods = 1;
	}

	const data: SearchAnalytics = {
		action: 'search',
		categoryCount,
		categoryId: filters.category?.id ?? 0,
		filters: filtersToSend,
		itemCount,
		...(resolvedLocality && { locality: localityToAnalyticsLocality(resolvedLocality) }),
		myProfile: store.myProfile,
		prevPageType: useCurrentPageType ? store.pageType : store.prevPageType,
		queryValue: filters.hledej.seoName || '',
		searchType,
		sellerFilter: store.sellerFilter,
		tab: tab,
		usedGoods: store.usedGoods,
		...(store.sellerId && { sellerId: store.sellerId }),
	};

	return DOTHelper.hit(data);
}

export type TabSearchAnalytics = {
	action: 'tabSearch';
	categoryId: string;
	filters: SearchFiltersAnalytics;
	itemCount: number;
	locality?: LocalityAnalytics;
	myProfile: BooleanAnalytics;
	prevPageType: PrevPageType;
	queryValue?: string;
	searchType: SearchType;
	sellerFilter: BooleanAnalytics;
	sellerId?: number | string;
	tab: HomepageTab;
	usedGoods: BooleanAnalytics;
};

export function sendTabSearchAnalytics({
	categoryId,
	filters,
	itemCount,
	resolvedLocality,
	searchType,
	tab,
	usedGoods,
}: {
	categoryId: number;
	filters: SearchFiltersAnalytics;
	itemCount: number;
	resolvedLocality?: LocalityData;
	searchType: SearchType;
	tab: HomepageTab;
	usedGoods: boolean;
}) {
	// #if [DEV]
	DOTHelper.checkStore(SEARCH_KEYS);
	// #endif

	const store = DOTHelper.getStore();
	DOTHelper.addToStore({ searchType, itemCount, tab }); // searchType will be used for saving offerDetailMeta when clicking on an offerCard

	const data: TabSearchAnalytics = {
		action: 'tabSearch',
		categoryId: categoryId.toString(),
		filters: filters,
		itemCount,
		...(resolvedLocality && { locality: localityToAnalyticsLocality(resolvedLocality) }),
		myProfile: 0,
		prevPageType: store.pageType,
		queryValue: '',
		searchType,
		sellerFilter: 0,
		tab,
		usedGoods: DOTHelper.sanitizeBoolean(usedGoods),
	};

	return DOTHelper.hit(data);
}

export type MoreZboziOffersAnalytics = {
	action: 'goToZbozi';
	searchType: SearchType;
	itemCount: number;
	categoryId: string;
	queryValue: string;
};

export function sendMoreZboziOffersAnalytics(filters: URLFilterParams, itemCount: number) {
	const store = DOTHelper.getStore();

	const data: MoreZboziOffersAnalytics = {
		action: 'goToZbozi',
		searchType: store.searchType,
		itemCount,
		categoryId: (filters.category?.id ?? 0).toString(),
		queryValue: filters.hledej.seoName || '',
	};

	return DOTHelper.hit(data);
}

export type SearchPageImpression = {
	pageType: typeof PageType.SEARCH_RESULTS;
	routeParamCategoryId?: string;
	routeParamCategoryName?: string;
	routeParamLocality?: string;
	routeParamPrice?: string;
	routeParamSort?: string;
	routeParamStrana?: string;
	routeParamWatchdog_hash?: string;
	routeParamWatchdog_id?: string;
	routeParamWatchdog_timestamp?: string;
};

export function sendSearchPageImpression() {
	const data: SearchPageImpression = {
		pageType: PageType.SEARCH_RESULTS,
		...getSearchPageUrlParams(),
	};

	DOTHelper.impress(data);
}
